// Auto-shrinking text that fits its container — both width AND height.
// Works inside html2canvas because we use useLayoutEffect (runs before paint),
// and the final font-size is baked into the inline style → preserved by cloneNode.
//
// The wrapper is `display: block` with `width: 100%` so the element fills the
// flex parent and `el.scrollHeight` / `el.scrollWidth` accurately reflect
// content overflow (flex containers' own scroll metrics are unreliable when
// children overflow, which previously caused long text to spill outside
// regular bingo cells while music-cells — which use explicit row heights —
// worked fine).
import { useLayoutEffect, useRef, useState } from "react";

export default function AutoFitText({
  text,
  className = "",
  style = {},
  maxFontPx = 18,
  minFontPx = 4,
  step = 0.5,
  // Extra safety: after the algorithm finds a fitting size, shrink it by this
  // many additional steps so descenders / line-spacing variations never bleed
  // outside the container.
  safetySteps = 2,
}) {
  const ref = useRef(null);
  const [fontPx, setFontPx] = useState(maxFontPx);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;

    let size = maxFontPx;
    el.style.fontSize = `${size}px`;
    // Force layout sync each iteration via reading scrollWidth/Height.
    // We measure the ELEMENT itself (not the parent) because flex containers
    // can report stale scroll metrics when their children overflow — the
    // child's scroll/offset measurements are the ground truth.
    let guard = 200;
    const fits = () => {
      const cw = parent.clientWidth;
      const ch = parent.clientHeight;
      // Use Math.max of scroll & offset for the element so we catch both
      // wrap-induced overflow (scrollHeight) and intrinsic content overflow.
      const w = Math.max(el.scrollWidth, el.offsetWidth);
      const h = Math.max(el.scrollHeight, el.offsetHeight);
      return w <= cw + 1 && h <= ch + 1;
    };
    while (guard-- > 0 && size > minFontPx && !fits()) {
      size -= step;
      el.style.fontSize = `${size}px`;
    }
    // Apply safety shrink so text never sits flush against the edge.
    for (let i = 0; i < safetySteps; i++) {
      if (size - step >= minFontPx) {
        size -= step;
      }
    }
    el.style.fontSize = `${size}px`;
    setFontPx(size);
  }, [text, maxFontPx, minFontPx, step, safetySteps]);

  return (
    <span
      ref={ref}
      className={className}
      style={{
        ...style,
        fontSize: `${fontPx}px`,
        lineHeight: 1.1,
        // `block` + width:100% so the element fills the flex parent and
        // both axes can be reliably measured. (Previously inline-block,
        // which caused regular-bingo cells to mis-measure on overflow.)
        display: "block",
        width: "100%",
        maxWidth: "100%",
        // NEVER break mid-word. The auto-fit algorithm shrinks the font
        // until the entire word fits on a line. Wrapping is only allowed
        // between whitespace-separated words.
        wordBreak: "keep-all",
        overflowWrap: "normal",
        whiteSpace: "normal",
        hyphens: "manual",
        letterSpacing: style.letterSpacing ?? "0",
      }}
    >
      {text}
    </span>
  );
}
